import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { EngagementUserIdTypeEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-user-id-type.enum';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { Attachment } from 'src/app/tasks/shared/models';

import { AlcmeonContextService } from '../../core/alcmeon-context/alcmeon-context.service';
import { AttachmentType, Editor2Attachment, MessageSender, TaskState } from '../../shared/models/misc.model';
import {
  API_URL_PREFIX,
  BasicService,
  JSON_CONTENT_TYPE_HEADER,
  RoutePaths,
} from '../../shared/services/basic.service';
import { ErrorHandlingService } from '../../shared/services/error-handling.service';
import { TagsService } from '../../shared/services/tags.service';
import { TaskService } from '../../shared/services/task.service';
import { ArchiveAndOtherActionsService } from '../archive-and-other-actions/archive-and-other-actions.service';
import { taskIdFromTaskOrActionId } from './id-conversions';
import {
  AfterSendAction,
  AfterSendActionType,
  AnswerAction,
  AnyTaskOrActionType,
  BackendLogAnswerRequest,
  LazyloadPublicContextMessageResponse,
  LazyloadTasksOrActionsRequest,
  LazyloadTasksOrActionsResponse,
  LogAnswerRequest,
  NormalizedTaskOrActionContent,
  ReleaseTaskRequest,
  ReleaseTaskResponse,
  Task,
  TaskForProcessingRequest,
  TaskForProcessingResponse,
  TaskOrActionType,
  TaskPublicContextResponse,
} from './model';
import { BZCSplittableRichLink, TwitterPublicContext } from './models';

export const MESSAGE_PAGE_SIZE = 20;

@Injectable({
  providedIn: 'root',
})
export class TaskProcessService {
  handleTypingBS = new BehaviorSubject<string>(undefined);
  bzcSplittableRichLinkMap: Map<string, BZCSplittableRichLink> = new Map();
  updateDeletedStatusBS = new Subject<string>();

  constructor(
    private http: HttpClient,
    private asyncRequestService: AsyncRequestService,
    private ehs: ErrorHandlingService,
    private taskService: TaskService,
    private alcmeonContextService: AlcmeonContextService,
    private tagsService: TagsService,
    private archiveAndOtherActionsService: ArchiveAndOtherActionsService,
    private basicService: BasicService
  ) {}

  emitEditorHandleTyping(user_external_id: string, siteId: number) {
    return this.http.post(
      `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContextService.companyId}/sites/${siteId}/typing-start`,
      { user_external_id }
    );
  }

  notifyTypingEnd(user_external_id: string, siteId: number) {
    return this.http.post(
      `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContextService.companyId}/sites/${siteId}/typing-end`,
      { user_external_id }
    );
  }

  getBZCUrlData(siteId, urls: string[]) {
    const calls: Observable<BZCSplittableRichLink>[] = (urls || []).map(url =>
      of(1).pipe(
        switchMap(() => {
          if (this.bzcSplittableRichLinkMap.has(url)) {
            return of(this.bzcSplittableRichLinkMap.get(url));
          }
          return this.asyncRequestService.post<BZCSplittableRichLink>(
            `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContextService.companyId}/sites/${siteId}/splittable-rich-links`,
            { url }
          );
        })
      )
    );
    from(calls)
      .pipe(concatMap(call => call))
      .subscribe({
        next: result => {
          if (result?.url) {
            this.basicService.handleBZCSplittableRichLinkBS.next(result);
            this.bzcSplittableRichLinkMap.set(result.url, result);
          }
        },
      });
  }

  clearBZCSplittableUrlAttachements() {
    this.basicService.handleClearBZCSplittableRichLinkAttachmentsBS.next(true);
  }

  getTaskForProcessing(taskId: number): Observable<TaskForProcessingResponse> {
    const request: TaskForProcessingRequest = {
      taskId,
      formatFirstNMessages: MESSAGE_PAGE_SIZE,
    };
    const taskForProcessingURL = `${API_URL_PREFIX}/mobile/task/search`;
    return this.http.post<TaskForProcessingResponse>(taskForProcessingURL, request, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(response => {
        if (!this.alcmeonContextService.isLoggedUserReadOnlyForSite(response.site.id)) {
          this.callAssigned(taskId);
        }
      }),
      tap(response => this.extractAnnotationAndHelpActionIds(response)),
      tap(response => this.tagsService.updateTagGroups(response, 'processingTask')),
      tap(response => {
        this.maybeUpdateDeletedStatus(response).subscribe(resp => {
          if (resp.error?.error_message_translation_key) {
            this.updateDeletedStatusBS.next(resp.error.error_message_translation_key);
          }
        });
      }),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  tryToSearchConversation(userExternalId: number, siteId: number): Observable<TaskForProcessingResponse> {
    const request: any = {
      userExternalId: `${userExternalId}`.replace(/^(\+)/i, ''),
      siteId,
      formatFirstNMessages: MESSAGE_PAGE_SIZE,
    };
    return this.http
      .post<TaskForProcessingResponse>(`${API_URL_PREFIX}/mobile/engagement/search`, request, JSON_CONTENT_TYPE_HEADER)
      .pipe(
        tap(response => this.extractAnnotationAndHelpActionIds(response)),
        this.ehs.catchAlertAndThrowHttpError()
      );
  }

  sendMessageEngagement(
    userExternalId: string,
    siteId: number,
    messageData: any,
    crm: any,
    channelType: SiteTypeEnum,
    userIdType?: EngagementUserIdTypeEnum
  ): Observable<TaskForProcessingResponse> {
    const request: any = {
      userExternalId: `${userExternalId}`.replace(/^(\+)/i, ''),
      siteId,
      messageData,
      crm,
    };

    if (channelType === SiteTypeEnum.Kakaotalk) {
      request.userIdType = userIdType || EngagementUserIdTypeEnum.PHONE_NUMBER;
    }

    return this.http
      .post<TaskForProcessingResponse>(`${API_URL_PREFIX}/mobile/engagement/send`, request, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  lazyloadTasksForProcessing(
    tasksOrActionsIds: string[],
    includeTags = false
  ): Observable<LazyloadTasksOrActionsResponse> {
    const request: LazyloadTasksOrActionsRequest = { tasksOrActionsIds, includeTags };
    const lazyloadURL = `${API_URL_PREFIX}/mobile/task/format`;
    return this.http
      .post<LazyloadTasksOrActionsResponse>(lazyloadURL, request, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  // This is not called directly by the methods above,
  // because the day indicator of the last message from the previous http call needs to be updated too
  setUIProperties(messages: AnyTaskOrActionType[], previouslyMostRecentMessage: AnyTaskOrActionType) {
    this.normalizeContent(messages);
    this.updateMessagesDayIndicators(messages, previouslyMostRecentMessage);
  }

  /**
   * Release a task, which means move it back to its previous state
   */
  releaseTask(taskId: number) {
    const releaseTaskURL = `${API_URL_PREFIX}/mobile/task/release`;
    const request: ReleaseTaskRequest = { taskId };
    return this.http.post<ReleaseTaskResponse>(releaseTaskURL, request, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(response => this.fireTaskStateUpdateAfterRelease(taskId, response)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  moveTaskToPending(taskId: number) {
    const url = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/move-to-pending`;
    const body = {};
    return this.http.post<any>(url, body, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(() => this.taskService.fireTaskStateUpdate(taskId, TaskState.Pending, null)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  fireTaskStateUpdateAfterRelease(taskId, releaseResponse: ReleaseTaskResponse) {
    const task = releaseResponse[taskId];
    this.taskService.fireTaskStateUpdate(taskId, task.state.taskStateCode, task.state.adminUser);
  }

  acquireOnHoldAndArchive(taskId: number) {
    return this.taskService
      .acquireTask(taskId, TaskState.OnHold)
      .pipe(mergeMap(() => this.archiveAndOtherActionsService.archiveTask(taskId)));
  }

  logAnswerThenExecuteAnswer(request: LogAnswerRequest, afterSendAction: AfterSendAction) {
    const body = this.convertLogAnswerRequestFrontendToBackend(request);
    const logAnswerURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${request.taskId}/log-answer`;

    // Curious about the mergeMap below? see https://stackoverflow.com/questions/34104638/how-to-chain-http-calls-in-angular2
    return this.http.post<any>(logAnswerURL, body, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(logAnswerResponse => this.alertAndThrowIfHttp200WithError(logAnswerResponse)),
      mergeMap(logAnswerResponse =>
        this.executeAnswer(request, afterSendAction, logAnswerResponse).pipe(
          finalize(() => this.basicService.hideSpinner())
        )
      ),
      mergeMap(() => {
        switch (afterSendAction.type) {
          case AfterSendActionType.Route:
            return this.tagAfterAnswer(request, afterSendAction);
          case AfterSendActionType.AskForHelp:
            return this.requestHelpAfterAnswer(request, afterSendAction);
          default:
            return of(null);
        }
      }),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  retrySendingAnswer(answer: AnswerAction) {
    const taskId = answer.answeredTaskId;
    const request = this.convertLogAnswerRequestBackendToFrontend(answer.error.logAnswerRequest, taskId);
    const afterSendAction = { type: AfterSendActionType.None };
    return this.taskService.aquireTaskFromAnyState(taskId).pipe(
      mergeMap(() => this.logAnswerThenExecuteAnswer(request, afterSendAction)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  canSendPrivateMessageAsOtherSite(siteId: number, replyTo: MessageSender): Observable<boolean> {
    const url = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContextService.companyId}/external-user/${replyTo.id}/can-send-private-message-as-other-site/${siteId}`;
    return this.asyncRequestService.get<any>(url).pipe(
      map(response => response.can_send_private_message_as_other_site),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  annotateTask(taskId: number, data: { comment: string; parent_id: number; put_on_hold: boolean }) {
    const annotateTaskURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/comments`;
    return this.http.post<any>(annotateTaskURL, data).pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  requestHelp(taskId: number, data: { message: string }) {
    const helpRequestURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/help/request`;
    return this.http.post<any>(helpRequestURL, data).pipe(
      tap(() => this.taskService.fireTaskStateUpdateByLoggedUser(taskId, TaskState.HelpRequested)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  answerHelpRequest(taskId: number, data: { help_request_id: number; comment: string }) {
    const answerHelpRequestURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/help/answer`;
    return this.http.post<any>(answerHelpRequestURL, data).pipe(
      tap(() => this.taskService.fireTaskStateUpdateByLoggedUser(taskId, TaskState.HelpAnswered)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  getTaskPublicContext(taskId: number) {
    const endpointURL = `${API_URL_PREFIX}/mobile/task-public-context/search`;
    const request = { taskId };
    return this.http
      .post<TaskPublicContextResponse>(endpointURL, request, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.ehs.catchAlertAndThrowHttpError());
  }

  lazyloadPublicContextMessages(contextMessageIds: number[], siteId: number) {
    const endpointURL = `${API_URL_PREFIX}/mobile/task-public-context/format`;
    const request = { contextMessageIds, siteId };
    return this.http.post<LazyloadPublicContextMessageResponse>(endpointURL, request, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(response => this.normalizeContent(Object.values(response.contextMessagesById))),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  archiveCurrentAndStartProcessingNewTask(fromTaskId, toTaskId): Observable<TaskForProcessingResponse> {
    const endpointURL = `${API_URL_PREFIX}/mobile/task/switch-tasks`;
    const request = { fromTaskId, toTaskId };
    return this.http
      .post<TaskForProcessingResponse>(endpointURL, request, { ...JSON_CONTENT_TYPE_HEADER, observe: 'response' })
      .pipe(
        tap((httpResponse: HttpResponse<any>) => {
          if (httpResponse.status !== 204) {
            const response = httpResponse.body;
            this.extractAnnotationAndHelpActionIds(response);
            this.tagsService.updateTagGroups(response, 'processingTask');
          }
        }),
        map((httpResponse: HttpResponse<any>) => httpResponse.body)
        // ignore errors for now, as the "new message bellow" button will be displayed anyway
      );
  }

  bubbleElementId(id: string) {
    return `message-bubble-${id}`;
  }

  private convertLogAnswerRequestFrontendToBackend(request: LogAnswerRequest): BackendLogAnswerRequest {
    return {
      answer: request.answers,
      is_private: request.isPrivate,
      reply_to: {
        // filter out avatarURL and isVIP
        id: request.replyTo.id,
        name: request.replyTo.name,
      },
      reply_to_user_ids: request.replyToUserIds,
      reply_as: request.replyAs,
    };
  }

  private convertLogAnswerRequestBackendToFrontend(request: BackendLogAnswerRequest, taskId: number): LogAnswerRequest {
    return {
      taskId,
      answers: request.answer,
      isPrivate: request.is_private,
      replyTo: request.reply_to,
      replyToUserIds: request.reply_to_user_ids,
      replyAs: request.reply_as,
    };
  }

  // The /log-answer and /execute-answer endpoints can return an error with a 200 http status;
  // if that's the case, we convert that response to an angular HttpErrorResponse
  private alertAndThrowIfHttp200WithError(logAnswerResponse) {
    if (logAnswerResponse.error) {
      const httpError = new HttpErrorResponse({ statusText: logAnswerResponse.error });
      // .error contains an already translated error message, but the translate service will happily leave it untouched
      this.ehs.toastAlertError(httpError, logAnswerResponse.error);
      throw httpError;
    }
  }

  private maybeUpdateDeletedStatus(response: TaskForProcessingResponse): Observable<any> {
    if (response.processingTask.shouldCallUpdateDeletedStatus) {
      const taskId = taskIdFromTaskOrActionId(response.processingTask.id);
      const url = `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContextService.companyId}/tasks/${taskId}/update-deleted-status`;
      return this.http.post<any>(url, {}, JSON_CONTENT_TYPE_HEADER).pipe(this.ehs.catchAlertAndThrowHttpError());
    }

    return of({});
  }

  getTwitterPublicContext(taskId: number): Observable<TwitterPublicContext[]> {
    return this.http
      .get<TwitterPublicContext[]>(
        `${API_URL_PREFIX}/api/1.0/companies/${this.alcmeonContextService.companyId}/tasks/${taskId}/twitter-public-context`
      )
      .pipe(
        catchError(() => of([])),
        map(([hierarchy, dict]) => {
          try {
            const map = (Object.keys(hierarchy) || []).reduce((result, id) => {
              (hierarchy[id] || []).forEach(child => {
                result.set(id, [].concat(result.get(id) || []).concat(dict[child]));
              });
              return result;
            }, new Map());

            const twitterPublicContextMessages = map.get('0');
            const groups = [];
            twitterPublicContextMessages.forEach(message => {
              const children = map.get(message.id) || [];
              message.groups = children.map((child, index) => {
                groups[index] = [].concat({ ...child });
                const addChildren = (messages = []) => {
                  messages.forEach(subChild => {
                    groups[index].push({ ...subChild });
                    const subChildren = map.get(subChild.id);
                    (subChildren || []).forEach(i => {
                      groups[index].push({ ...i });
                      if (map.get(i.id)?.length) {
                        addChildren(map.get(i.id));
                      }
                    });
                  });
                };
                addChildren(map.get(child.id) || []);
                return groups[index];
              });
            });

            return twitterPublicContextMessages;
          } catch {
            return [];
          }
        })
      );
  }

  private callAssigned(taskId: number): void {
    const taskAssignedURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/assigned`;
    this.http
      .post<void>(taskAssignedURL, {}, JSON_CONTENT_TYPE_HEADER)
      .pipe(this.ehs.catchAlertAndThrowHttpError())
      .subscribe();
  }

  private executeAnswer(logAnswerRequest: LogAnswerRequest, afterSendAction: AfterSendAction, logAnswerResponse) {
    const taskId = logAnswerRequest.taskId;
    const request = {
      reply_as: logAnswerRequest.replyAs,
      answer_tla_ids: logAnswerResponse.answer_tla_ids,
      answer_action_ids: logAnswerResponse.answer_action_ids,
      put_on_hold: afterSendAction.type === AfterSendActionType.PutOnHold,
      send_delayed_answer_immediately: afterSendAction.type === AfterSendActionType.TriggerSurvey,
    };
    const executeAnswerURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/execute-answer`;

    return this.http.post<any>(executeAnswerURL, request, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(executeAnswerResponse => this.alertAndThrowIfHttp200WithError(executeAnswerResponse)),
      tap(() => this.taskService.fireTaskStateUpdateByLoggedUser(taskId, TaskState.Answered)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  private tagAfterAnswer(request: LogAnswerRequest, afterSendAction: AfterSendAction) {
    return this.tagsService.updateTagValue(request.taskId, afterSendAction.details.tagIds, false).pipe(
      mergeMap(() => this.taskService.acquireTask(request.taskId, TaskState.Answered)),
      mergeMap(() => this.moveTaskToPending(request.taskId))
    );
  }

  private requestHelpAfterAnswer(request: LogAnswerRequest, afterSendAction: AfterSendAction) {
    const { message } = afterSendAction.details;
    return this.taskService
      .acquireTask(request.taskId, TaskState.Answered)
      .pipe(mergeMap(() => this.requestHelp(request.taskId, { message })));
  }

  /**
   * Text and attachment are set in different format and different places for different type of task/messages/action.
   * This method normalizes them before they are passed to <message-in-task-process>
   * Unfortunaltely we cannot use Editor2Attachment format
   * because the backend stores and returns attachment urls instead of picture/video ids for messages
   */
  private normalizeContent(messages: AnyTaskOrActionType[]) {
    for (const message of messages) {
      if (message) {
        switch (message.type) {
          case TaskOrActionType.Answer:
            message.content = this.normalizedContentForAnswer(message as AnswerAction);
            break;
          default:
            message.content = this.normalizedContentForOtherTypes(message as any);
            break;
        }
      }
    }
  }

  private normalizedContentForAnswer(message: AnswerAction): NormalizedTaskOrActionContent {
    const answersFromEditor2 = message.answer;
    const result = answersFromEditor2 ? answersFromEditor2[0] : { text: '', attachments: [] };
    if (!result.attachments) {
      result.attachments = [];
    }
    return result;
  }

  private normalizedContentForOtherTypes(message: Task): NormalizedTaskOrActionContent {
    const { text } = message;
    let attachments: Editor2Attachment[] = [];
    if ('shopProductInfo' in message) {
      attachments = [this.adaptShopProductInfo(message as any)];
    } else if ('attachments' in message) {
      attachments = message.attachments.map((attachment: Attachment) => this.adaptTaskOrMessageAttachment(attachment));
    }
    return { text, attachments };
  }

  private adaptShopProductInfo(task: Task): Editor2Attachment {
    if ('attachments' in task && task['attachments'].length > 0) {
      task.shopProductInfo.imageUrl = task.attachments[0].url;
    }
    return {
      type: AttachmentType.ShopProduct,
      value: task.shopProductInfo,
    };
  }

  private adaptTaskOrMessageAttachment(attachment: Attachment): Editor2Attachment {
    let type;
    const isValid = attachment.is_valid;
    let fileName = null;
    if (attachment.type.startsWith('image/')) {
      type = AttachmentType.Picture;
    } else if (attachment.type.startsWith('video/')) {
      type = AttachmentType.Video;
    } else if (attachment.type.startsWith('audio/')) {
      type = AttachmentType.Audio;
    } else if (attachment.type === 'link') {
      type = AttachmentType.Link;
    } else {
      type = AttachmentType.File;
      fileName = attachment.name;
    }
    let value;
    if (type === AttachmentType.Link) {
      if (isValid) {
        value = {
          linkLabel: this.basicService.translate(`attachments/${attachment.link_type}`),
          linkTarget: attachment.link,
        };
      } else {
        value = {
          linkLabel: this.basicService.translate(`attachments/invalid/${attachment.link_type}`),
        };
      }
    } else {
      value = {
        fileName,
        messageAttachementURL: attachment.url,
      };
    }
    return {
      type,
      value,
      isValid,
    };
  }

  /**
   * Computes which messages should display a date above them
   * (so that all messages of the same day are grouped under that day),
   * and sets the corresponding formatted date to these messages.
   *
   * A message should display a date indicator either if it is the oldest,
   * or if the previous message (chronologically) was sent on a different day
   */
  private updateMessagesDayIndicators(messages, previouslyMostRecentMessage) {
    const msInADay = 1000 * 60 * 60 * 24;

    // if no previously latest message provided, use 1970-01-01 as the time of the initial 'previous' message
    let previousMessageDate = new Date(previouslyMostRecentMessage ? previouslyMostRecentMessage.datetime : 0);
    for (const message of messages) {
      if (message) {
        const messageDate = new Date(message.datetime);
        const messageDay = Math.floor(messageDate.getTime() / msInADay); // TODO use date pipe with user's timezone to detect day changes
        const previousMessageDay = Math.floor(previousMessageDate.getTime() / msInADay);
        if (messageDay !== previousMessageDay) {
          message.dayIndicatorFormat = 'd MMMM';
          if (messageDate.getFullYear() !== new Date().getFullYear()) {
            message.dayIndicatorFormat += ' yyyy';
          }
        } else {
          message.dayIndicatorFormat = null; // this will prevent an indicator from appearing
        }
        previousMessageDate = messageDate;
      }
    }
  }

  private extractAnnotationAndHelpActionIds(response: TaskForProcessingResponse) {
    response.allAnnotationIds = response.tasksOrActionsIds.filter(id => id.includes(TaskOrActionType.Annotation));
    response.allHelpRequestAndResponseIds = response.tasksOrActionsIds.filter(
      id => id.includes(TaskOrActionType.HelpRequest) || id.includes(TaskOrActionType.HelpAnswer)
    );
  }

  reloadTaskProcessStateWithNewTask(currentTaskState: string, newTaskId: number, isCRMMode: boolean) {
    const navigationParams = {
      // we pass currentTaskState as a query param, as the location state is not yet updated when the guard is called
      queryParams: {
        'current-task-state': currentTaskState,
      },
    };
    const prefix = isCRMMode ? RoutePaths.TaskProcessForCrmPrefix : RoutePaths.TaskProcessPrefix;
    // trick the router into reloading the current route, see https://stackoverflow.com/a/49509706
    this.basicService
      .navigateRoot(RoutePaths.CulDeSac, { skipLocationChange: true })
      .then(() => this.basicService.navigateRoot(`${prefix}/${newTaskId}`, navigationParams));
  }

  anonymizeMessage(taskId: number) {
    const anonymizeTaskURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/anonymize`;
    return this.http.post<any>(anonymizeTaskURL, {}).pipe(this.ehs.catchAlertAndThrowHttpError());
  }
}
